import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import classNames from "classnames";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { CommonIcons, IntroEventCard, SuggestionsWidget } from "@42maru/webchat";
import { INVALID_CODES } from "@/core/validation";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { clickFromKeyboard } from "@/util/keyboardUtil";
import {
    isValidIntroTitleText,
    isValidIntroSubtitleText,
    INTRO_TITLE_MAX_LENGTH,
    INTRO_SUBTITLE_MAX_LENGTH
} from "@/util/helper/webchatThemeHelper";
import { useUploadedFiles, useSyncForm } from "@/hook";
import Button from "@/component/Button";
import Tooltip from "@/component/Tooltip";
import { ImageUploadBtn, SwitchInput, TextInputGroup } from "@/component/input";
import { DateRangePicker, DateRange } from "@/component/dateRangePicker";
import { SortableItem, SortableItemWrapper, SortableWrapper, toSortableItem } from "@/component/SortableWrapper";
import ChevronArrowIcon from "@/component/icon/ChevronArrowIcon";
import ClickableImg from "@/component/ClickableImg";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";

import { WebchatThemeFormProps } from ".";


const INTRO_ICON_SIZE = 64;
const EVENT_CARD_IMAGE_SIZE = 24;
const MIN_SUGGESTION_COUNT = 3;

const ChatHomeInputGroup: React.FC<WebchatThemeFormProps & {disabled?: boolean}> = (props) => {
    const { theme, disabled = false, legend, onChange } = props;

    const intl = useIntl();
    const { add, remove } = useUploadedFiles();

    const [disableEventCards, setDisableEventCards] = useState<boolean>(
        theme.introCardOrder?.find(item => item.key === "event_card")?.disable ?? false
    );

    const titleValidation = useMemo(() => {
        const validation = isValidIntroTitleText(theme.introTitleText ?? "");
        const errorText = validation.code === INVALID_CODES.MAX
            ? intl.formatMessage({ id: "i09049" }, { maxLength: INTRO_TITLE_MAX_LENGTH })
            : undefined;
        return {
            isValid: validation.isValid,
            errorText
        };
    }, [theme.introTitleText, intl]);

    const subtitleValidation = useMemo(() => {
        const validation = isValidIntroSubtitleText(theme.introSubtitleText ?? "");
        const errorText = validation.code === INVALID_CODES.MAX
            ? intl.formatMessage({ id: "i09049" }, { maxLength: INTRO_SUBTITLE_MAX_LENGTH })
            : undefined;
        return {
            isValid: validation.isValid,
            errorText
        };
    }, [theme.introSubtitleText, intl]);

    const onUploadIcon = (file: File, url: string) => {
        if (theme.introTitleLogoUrl) {
            remove(theme.introTitleLogoUrl);
        }
        add(file, url);
        onChange({ ...theme, introTitleLogoUrl: url });
    };

    const onRemoveIcon = () => {
        const url = theme.introTitleLogoUrl;
        if (url) {
            remove(url);
        }
        onChange({ ...theme, introTitleLogoUrl: undefined });
    };

    const onChangeSuggestions = (suggestions: string[]) => {
        const widget = theme.widgets ?? [];
        const idx = widget.findIndex(widget => widget.type === "suggestions");
        if (idx === -1) {
            onChange({
                ...theme,
                widgets: [...widget, { type: "suggestions", disable: false, data: suggestions } as SuggestionsWidget]
            });
        } else {
            const suggestionWidget = widget[idx];
            onChange({
                ...theme,
                widgets: withArray(widget, idx, { ...suggestionWidget, disable: false, data: suggestions } as SuggestionsWidget)
            });
        }
    };

    const onChangeDisableEventCards = (disable: boolean) => {
        setDisableEventCards(disable);
        const cardOrder = theme.introCardOrder ?? [];
        const index = cardOrder.findIndex(item => item.key === "event_card") ?? cardOrder.length;
        onChange({
            ...theme,
            introCardOrder: withArray(
                cardOrder,
                index,
                {
                    ...(cardOrder[index] ?? {}),
                    key: "event_card" as const,
                    disable
                }
            )
        });
    };

    const suggestions = useMemo(() => {
        const widgets = (theme as any).widgets ?? [];
        const suggestionWidget = widgets.find((widget: any) => widget.type === "suggestions");
        return suggestionWidget?.data ?? [];
    }, [theme]);

    return (
        <fieldset className="flex flex-col gap-6">
            <legend className="sr-only">
                {legend}
            </legend>
            <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                    <label htmlFor="webchat-show-intro-icon"
                           className={classNames("field-label", disabled && "field-label-disabled")}
                    >
                        {intl.formatMessage({ id: "b0059" })}
                    </label>
                    <SwitchInput id="webchat-show-intro-icon"
                                 disabled={disabled}
                                 defaultValue={theme.showIntroIcon ?? false}
                                 onSwitch={v => onChange({ ...theme, showIntroIcon: v })}
                    />

                </div>
                {Boolean(theme.showIntroIcon) && (
                    <div data-testid="intro-logo-area"
                         className="flex items-end gap-4"
                    >
                        {theme.introTitleLogoUrl ? (<>
                            <div className="size-[64px] rounded-full overflow-hidden">
                                <ClickableImg src={theme.introTitleLogoUrl}
                                              alt={intl.formatMessage({ id: "b0059" })}
                                              height={INTRO_ICON_SIZE}
                                              width={INTRO_ICON_SIZE}
                                />
                            </div>
                            <button disabled={disabled}
                                    aria-label={intl.formatMessage({ id: "b0006" })}
                                    onClick={onRemoveIcon}
                            >
                                <DeleteIcon className="text-point-red size-5" />
                            </button>
                        </>) : (<>
                            <div className="size-[64px] rounded-full overflow-hidden">
                                {CommonIcons.introDefaultIcon()}
                            </div>
                            <ImageUploadBtn disabled={disabled}
                                            onUpload={(file, url) => onUploadIcon(file, url)}
                            />
                        </>)}
                    </div>
                )}
            </div>


            <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1.5">
                    <label htmlFor="webchat-setting-intro-title"
                           className={classNames("field-label", disabled && "field-label-disabled")}
                    >
                        {intl.formatMessage({ id: "i09039" })}
                    </label>
                    <TextInputGroup id="webchat-setting-intro-title"
                                    disabled={disabled}
                                    value={theme.introTitleText ?? ""}
                                    placeholder={intl.formatMessage({ id: "i09047" })}
                                    invalid={!titleValidation.isValid}
                                    invalidText={titleValidation.errorText}
                                    onChange={value => onChange({ ...theme, introTitleText: value || undefined })}
                    />
                </div>

                <div className="flex flex-col gap-1.5">
                    <div className="flex items-center gap-1">
                        <label htmlFor="webchat-setting-intro-subtitle"
                               className={classNames("field-label", disabled && "field-label-disabled")}
                        >
                            {intl.formatMessage({ id: "i09029" })}
                        </label>
                        <div className="text-[13px] font-[400] text-black-400">
                            {intl.formatMessage({ id: "i09030" })}
                        </div>
                    </div>
                    <TextInputGroup id="webchat-setting-intro-subtitle"
                                    value={theme.introSubtitleText ?? ""}
                                    disabled={disabled}
                                    invalid={!subtitleValidation.isValid}
                                    invalidText={subtitleValidation.errorText}
                                    placeholder={intl.formatMessage({ id: "i09038" })}
                                    onChange={value => onChange({ ...theme, introSubtitleText: value || undefined })}
                    />
                </div>
            </div>

            <div className="flex flex-col gap-2">
                <div className={classNames("field-label", disabled && "field-label-disabled")}>
                    {intl.formatMessage({ id: "i09101" })}
                </div>
                <div className="flex flex-col gap-2">
                    <div className="webchat-setting-inner-card">
                        <Disclosure>
                            <DisclosureButton className="webchat-setting-inner-card-header">
                                {({ open }) => (<>
                                    <div className={classNames("field-label text-start", disabled && "field-label-disabled")}>
                                        {intl.formatMessage({ id: "i09094" })}
                                    </div>
                                    <div className="col-span-2 flex items-center justify-end">
                                        <ChevronArrowIcon direction={open ? "up" : "down"}
                                                          className="size-4 text-black-300 transition-transform duration-200"
                                        />
                                    </div>
                                </>)}
                            </DisclosureButton>
                            <DisclosurePanel transition
                                             className="transition-disclosure border-t border-black-200 data-[closed]:border-transparent"
                            >
                                <SortableSuggestionList disabled={disabled}
                                                        title={intl.formatMessage({ id: "i09094" })}
                                                        defaultSuggestions={suggestions}
                                                        onChange={onChangeSuggestions}
                                />
                            </DisclosurePanel>
                        </Disclosure>
                    </div>

                    <div className="webchat-setting-inner-card">
                        <Disclosure>
                            <DisclosureButton as="div"
                                              tabIndex={0}
                                              className="webchat-setting-inner-card-header cursor-pointer"
                                              onKeyDown={clickFromKeyboard}
                            >
                                {({ open }) => (<>
                                    <div id="webchat-event-cards"
                                         className={classNames("field-label text-start", disabled && "field-label-disabled")}
                                    >
                                        {intl.formatMessage({ id: "i09096" })}
                                    </div>
                                    <SwitchInput aria-labelledby="webchat-event-cards"
                                                 size="sm"
                                                 disabled={disabled}
                                                 defaultValue={!disableEventCards}
                                                 onSwitch={v => onChangeDisableEventCards(!v)}
                                    />
                                    <ChevronArrowIcon direction={open ? "up" : "down"}
                                                      className="size-4 text-black-300 transition-transform duration-200"
                                    />
                                </>)}
                            </DisclosureButton>
                            <DisclosurePanel transition
                                             className="transition-disclosure border-t border-black-200 data-[closed]:border-transparent"
                            >
                                <SortableEventCardList disabled={disabled}
                                                       eventCards={theme.introEventCards ?? []}
                                                       onChange={cards => onChange({ ...theme, introEventCards: cards })}
                                />
                            </DisclosurePanel>
                        </Disclosure>
                    </div>
                    <div className="webchat-setting-inner-card">
                        <div className="webchat-setting-inner-card-header">
                            <label htmlFor="webchat-show-intro-footer"
                                   className={classNames("field-label", disabled && "field-label-disabled")}
                            >
                                {intl.formatMessage({ id: "i09100" })}
                            </label>
                            <div className="col-span-2 flex items-center">
                                <SwitchInput id="webchat-show-intro-footer"
                                             size="sm"
                                             disabled={disabled}
                                             defaultValue={theme.showIntroFooter}
                                             onSwitch={value => onChange({ ...theme, showIntroFooter: value })}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </fieldset>
    );
};


interface SortableSuggestionListProps {
    title: string;
    defaultSuggestions: string[];
    disabled?: boolean;
    onChange: (suggestions: string[]) => void;
}

const SortableSuggestionList: React.FC<SortableSuggestionListProps> = (props) => {
    const { title, defaultSuggestions, disabled, onChange } = props;

    const intl = useIntl();

    const [sortableSuggestions, setSortableSuggestions] = useState<SortableItem<string>[]>([]);

    const update = (suggestions: SortableItem<string>[]) => {
        setSortableSuggestions(suggestions);
        onChange(suggestions.map(item => item.data).filter(item => item !== ""));
    };

    useEffect(() => {
        const newSuggestions = [...defaultSuggestions];
        while (newSuggestions.length < MIN_SUGGESTION_COUNT) {
            newSuggestions.push("");
        }
        setSortableSuggestions(newSuggestions.map((suggestion, index) => ({ id: index.toString(), data: suggestion })));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <SortableWrapper disabled={disabled}
                         label={title}
                         items={sortableSuggestions}
                         className="py-5 px-4 flex flex-col gap-2"
                         onDragSortEnd={update}
        >{sortableSuggestions.map((item, index) => (
            <SortableItemWrapper key={item.id}
                                item={item}
            >{dragHandle => (
                <div className="w-full flex items-center gap-2">
                    <div className={classNames(disabled && "text-black-300 pointer-events-none")}>
                        {dragHandle}
                    </div>
                    <input type="text"
                           disabled={disabled}
                           value={item.data}
                           placeholder={intl.formatMessage({ id: "i09095" })}
                           aria-label={`${title} ${index + 1}`}
                           className="input-default"
                           onChange={e => update(withArray(sortableSuggestions, index, { ...item, data: e.target.value }))}
                    />
                </div>
            )}</SortableItemWrapper>))}
        </SortableWrapper>
    );
};


interface SortableEventCardListProps {
    eventCards: IntroEventCard[];
    disabled?: boolean;
    onChange: (cards: IntroEventCard[]) => void;
}

const SortableEventCardList: React.FC<SortableEventCardListProps> = (props) => {
    const { eventCards, disabled, onChange } = props;

    const intl = useIntl();
    const [sortableIntroEventCards, setSortableIntroEventCards] = useState<SortableItem<IntroEventCard>[]>(eventCards?.map(toSortableItem) ?? []);

    const update = (cards: SortableItem<IntroEventCard>[]) => {
        setSortableIntroEventCards(cards);
        onChange(cards.map(item => item.data));
    };

    const onChangeCard = (index: number, key: keyof IntroEventCard, value: IntroEventCard[keyof IntroEventCard]) => {
        const data = { ...sortableIntroEventCards[index].data, [key]: value };
        update(withArray(sortableIntroEventCards, index, { ...sortableIntroEventCards[index], data }));
    };

    const onChangePeriod = (index: number, period: DateRange) => {
        const data = {
            ...sortableIntroEventCards[index].data,
            startDate: period.start?.format("YYYY-MM-DD HH:mm:ss"),
            endDate: period.end?.format("YYYY-MM-DD HH:mm:ss")
        };
        update(withArray(sortableIntroEventCards, index, { ...sortableIntroEventCards[index], data }));
    };

    const syncFromProps = useCallback(() => {
        if (!eventCards) {
            return;
        }
        setSortableIntroEventCards(eventCards.map(toSortableItem));
    }, [eventCards]);

    useSyncForm(syncFromProps);

    return (
        <SortableWrapper disabled={disabled}
                         label={intl.formatMessage({ id: "i09096" })}
                         items={sortableIntroEventCards}
                         className="w-full py-5 px-4 flex flex-col gap-6"
                         onDragSortEnd={update}
        >{sortableIntroEventCards.map((item, index) => (
            <SortableItemWrapper key={item.id}
                                 item={item}
                                 aria-label={`${intl.formatMessage({ id: "i09108" })} ${index + 1}`}
            >{dragHandle => (
                <EventCardItem index={index}
                               disabled={disabled}
                               eventCard={item.data}
                               dragHandle={dragHandle}
                               onChange={(key, value) => onChangeCard(index, key, value)}
                               onChangePeriod={period => onChangePeriod(index, period)}
                               onDelete={() => update(toSpliced(sortableIntroEventCards, index, 1))}
                />
            )}</SortableItemWrapper>))}
            <div className="w-full h-6 flex items-center justify-center">
                <Button variant="text-transparent"
                        disabled={disabled}
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => {
                            const today = moment().startOf('day');
                            const tomorrow = today.clone().add(1, 'day');
                            update([
                                ...sortableIntroEventCards,
                                toSortableItem({
                                    startDate: today.format("YYYY-MM-DD HH:mm:ss"),
                                    endDate: tomorrow.format("YYYY-MM-DD HH:mm:ss")
                                })
                            ]);
                        }}
                >
                    <AddIcon className="size-4" />
                </Button>
            </div>
        </SortableWrapper>
    );
};


interface EventCardItemProps {
    index: number;
    eventCard: IntroEventCard;
    disabled?: boolean;
    dragHandle: React.ReactNode;
    onChange: (key: keyof IntroEventCard, value: IntroEventCard[keyof IntroEventCard]) => void;
    onChangePeriod: (period: DateRange) => void;
    onDelete: () => void;
}

const EventCardItem: React.FC<EventCardItemProps> = (props) => {
    const { index, eventCard, disabled, dragHandle, onChange, onChangePeriod, onDelete } = props;

    const intl = useIntl();

    const { add, remove } = useUploadedFiles();

    const onUploadImage = (file: File, url: string) => {
        if (eventCard.imageUrl) {
            remove(eventCard.imageUrl);
        }
        add(file, url);
        onChange("imageUrl", url);
    };

    const onDeleteImage = () => {
        const url = eventCard.imageUrl;
        if (url) {
            remove(url);
        }
        onChange("imageUrl", undefined);
    };

    const momentPeriod = useMemo(() => {
        return {
            start: eventCard.startDate ? moment(eventCard.startDate) : undefined,
            end: eventCard.endDate ? moment(eventCard.endDate) : undefined
        };
    }, [eventCard.startDate, eventCard.endDate]);

    return (
        <div className="flex flex-col">

            <div className="h-6 flex items-center gap-2">
                <div className={classNames(disabled && "text-black-300 pointer-events-none")}>
                    {dragHandle}
                </div>
                <div className={classNames("field-label", disabled && "field-label-disabled")}>
                    {`${intl.formatMessage({ id: "i09108" })} ${index + 1}`}
                </div>
                <button disabled={disabled}
                        aria-label={intl.formatMessage({ id: "b0006" })}
                        className="flex-none"
                        onClick={onDelete}
                >
                    <DeleteIcon className={classNames("size-4", !disabled ? "text-point-red" : "text-point-red-50")} />
                </button>
            </div>

            <div className="mt-2 flex items-center gap-2">
                <input type="text"
                       disabled={disabled}
                       value={eventCard.text ?? ""}
                       placeholder={intl.formatMessage({ id: "i09102" })}
                       aria-label={intl.formatMessage({ id: "i09102" })}
                       className="input-default"
                       onChange={e => onChange("text", e.target.value)}
                />
                <div className="h-input-default flex items-center gap-4 flex-none">
                    {eventCard.imageUrl ? (<>
                        <ClickableImg src={eventCard.imageUrl}
                                      alt={intl.formatMessage({ id: "i09105" })}
                                      height={EVENT_CARD_IMAGE_SIZE}
                                      width={EVENT_CARD_IMAGE_SIZE}
                        />
                        <button disabled={disabled}
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={onDeleteImage}
                        >
                            <DeleteIcon className="text-point-red size-4" />
                        </button>
                    </>) : (
                        <ImageUploadBtn disabled={disabled}
                                        onUpload={onUploadImage}
                        />
                    )}
                </div>  
            </div>

            <input type="text"
                   aria-label={intl.formatMessage({ id: "i09103" })}
                   className="input-default mt-1"
                   disabled={disabled}
                   value={eventCard.link ?? ""}
                   placeholder={intl.formatMessage({ id: "i09103" })}
                   onChange={e => onChange("link", e.target.value)}
            />

            <div className="mt-1.5 flex items-center gap-1">
                <DateRangePicker useFuture
                                 useTime
                                 disabled={disabled}
                                 period={momentPeriod}
                                 onChangePeriod={onChangePeriod}
                >
                    <Tooltip message={intl.formatMessage({ id: "i09104" })}>
                        <Button variant="outline-secondary"
                                disabled={disabled}
                                aria-label={intl.formatMessage({ id: "i00037" })}
                                className="flex-none w-[246px] px-2 py-[7px] items-center justify-between rounded-[6px] text-[12px] text-black-500 leading-none"
                        >
                            <div>{momentPeriod.start?.clone().locale('en').format("YYYY-MM-DD hh:mm A")}</div>
                            <div>~</div>
                            <div>{momentPeriod.end?.clone().locale('en').format("YYYY-MM-DD hh:mm A")}</div>
                        </Button>
                    </Tooltip>
                </DateRangePicker>
            </div>
        </div>
    );
};

export default ChatHomeInputGroup;
